/**
 * Organization Setup Page
 *
 * Displayed after user selects a pricing tier.
 * Collects organization name and optional description, then creates the organization.
 */

import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom'
import { useAuth } from './auth-context'
import * as orgApi from './api/organizations'
import './OrgSetup.css'

// Plan details for display
const PLAN_DETAILS = {
  standard: {
    name: 'Standard',
    price: 250,
    period: 'month',
    members: 5,
    surveys: 10,
  },
  business: {
    name: 'Business',
    price: 1000,
    period: 'month',
    members: 20,
    surveys: 50,
  },
  enterprise: {
    name: 'Enterprise',
    price: null,
    period: 'custom',
    members: 'Unlimited',
    surveys: 'Unlimited',
  },
}

function OrgSetup() {
  const { user, loading, refreshOrganizations } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const selectedPlan = searchParams.get('plan')

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    contactEmail: '',
    contactPhone: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Check for stored plan from login redirect
  useEffect(() => {
    if (!selectedPlan) {
      const storedPlan = sessionStorage.getItem('selectedPlan')
      if (storedPlan) {
        sessionStorage.removeItem('selectedPlan')
        navigate(`/org-setup?plan=${storedPlan}`, { replace: true })
      }
    }
  }, [selectedPlan, navigate])

  // Redirect if not logged in
  if (!loading && !user) {
    return <Navigate to="/login" replace />
  }

  // Redirect if no valid plan selected
  if (!selectedPlan || !PLAN_DETAILS[selectedPlan]) {
    return <Navigate to="/pricing" replace />
  }

  const planInfo = PLAN_DETAILS[selectedPlan]

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      setError('Organization name is required')
      return
    }

    if (formData.name.trim().length < 3) {
      setError('Organization name must be at least 3 characters')
      return
    }

    if (!formData.contactEmail.trim()) {
      setError('Contact email is required')
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      setError('Please enter a valid contact email')
      return
    }

    try {
      setSubmitting(true)
      setError('')

      // Generate slug from org name
      const slug =
        formData.name
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '') + `-${Date.now().toString(36)}`

      await orgApi.createOrganization({
        name: formData.name.trim(),
        slug,
        description: formData.description.trim() || null,
        subscriptionPlanCode: selectedPlan,
        settings: {
          contactEmail: formData.contactEmail.trim(),
          contactPhone: formData.contactPhone.trim() || null,
        },
      })

      // Refresh organizations and wait a moment for state to propagate
      await refreshOrganizations()

      // Small delay to ensure React state updates have propagated
      await new Promise((resolve) => setTimeout(resolve, 100))

      // Redirect to organization settings so the owner can invite team members
      navigate('/organization?welcome=true')
    } catch (err) {
      console.error('[OrgSetup] Failed to create organization:', err)
      setError(err.message || 'Failed to create organization. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="org-setup-page">
        <div className="org-setup-loading">
          <div className="loading-spinner">
            <div className="spinner-ring"></div>
          </div>
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="org-setup-page">
      <div className="org-setup-container">
        <div className="org-setup-header">
          <h1>Set Up Your Organization</h1>
          <p>Create your organization to get started with the {planInfo.name} plan</p>
        </div>

        {/* Plan Summary Card */}
        <div className="plan-summary-card">
          <div className="plan-summary-header">
            <span className="plan-badge">{planInfo.name}</span>
            <div className="plan-price">
              {planInfo.price !== null ? (
                <>
                  <span className="currency">₹</span>
                  <span className="amount">{planInfo.price}</span>
                  <span className="period">/{planInfo.period}</span>
                </>
              ) : (
                <span className="custom-price">Custom Pricing</span>
              )}
            </div>
          </div>
          <div className="plan-summary-features">
            <div className="feature-item">
              <span className="feature-icon">👥</span>
              <span>{planInfo.members} team members</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📋</span>
              <span>{planInfo.surveys} surveys</span>
            </div>
          </div>
        </div>

        {/* Organization Form */}
        <form onSubmit={handleSubmit} className="org-setup-form">
          {error && <div className="form-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="name">Organization Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter your organization name"
              required
              autoFocus
              disabled={submitting}
            />
            <span className="form-hint">This will be visible to all team members</span>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description (Optional)</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Brief description of your organization"
              rows={3}
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="contactEmail">Contact Email *</label>
            <input
              type="email"
              id="contactEmail"
              name="contactEmail"
              value={formData.contactEmail}
              onChange={handleInputChange}
              placeholder="contact@company.com"
              required
              disabled={submitting}
            />
            <span className="form-hint">For billing and important notifications</span>
          </div>

          <div className="form-group">
            <label htmlFor="contactPhone">Phone Number (Optional)</label>
            <input
              type="tel"
              id="contactPhone"
              name="contactPhone"
              value={formData.contactPhone}
              onChange={handleInputChange}
              placeholder="+91 98765 43210"
              disabled={submitting}
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/pricing')}
              disabled={submitting}>
              Back to Pricing
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting || !formData.name.trim() || !formData.contactEmail.trim()}>
              {submitting ? 'Creating...' : 'Create Organization'}
            </button>
          </div>
        </form>

        <p className="setup-note">
          You'll be the owner of this organization and can invite team members after setup.
        </p>
      </div>
    </div>
  )
}

export default OrgSetup
